import { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import TextField from "app/components/sessions/TextField";
import { changePassword } from "app/services/company/userService";
import { userLoggedIn } from "app/redux/auth/authSlice";

const buildPasswordChangeSchema = (t) =>
  yup.object().shape({
    password: yup
      .string()
      .min(8, t("passwordChange.validation.passwordMin"))
      .required(t("passwordChange.validation.passwordRequired")),
    confirm: yup
      .string()
      .oneOf([yup.ref("password"), null], t("passwordChange.validation.confirmMismatch"))
      .required(t("passwordChange.validation.confirmRequired"))
  });

export default function ForcePasswordChangeModal({ show, onClose }) {
  const { t } = useTranslation("settings");
  const dispatch = useDispatch();
  const { user, accessToken } = useSelector((s) => s.auth);
  const companyName = useSelector((s) => s.auth.user?.company?.name);

  const [submitting, setSubmitting] = useState(false);
  const schema = useMemo(() => buildPasswordChangeSchema(t), [t]);

  useEffect(() => {}, [show]);

  const handleSubmit = async ({ password }) => {
    if (!user) return;
    try {
      setSubmitting(true);
      await changePassword({ userId: user.id || user._id, newPassword: password });

      const updatedUser = { ...user, mustChangePassword: false };
      localStorage.setItem(
        "auth_user",
        JSON.stringify({ accessToken: accessToken, user: updatedUser })
      );
      dispatch(userLoggedIn({ accessToken: accessToken, user: updatedUser }));
      onClose?.();
    } catch (e) {
      const rawMsg = e?.response?.data?.message || e?.message;
      const displayMsg = Array.isArray(rawMsg) ? rawMsg.join(", ") : (rawMsg || t("passwordChange.alerts.changeError"));
      toast.error(displayMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Choose image based on user.title (served from public/assets)
  const avatarSrc =
    user?.title === "Mr"
      ? "/assets/icons/gender/male.png"
      : "/assets/icons/gender/female.png";

  return (
    <Modal show={show} onHide={() => {}} centered backdrop="static" keyboard={false}>
      <div
        className="p-4 text-center"
        style={{
          borderRadius: "20px",
          width: "100%",
          maxWidth: 520,
          display: "block"
        }}
      >
        {/* Avatar */}
        <div className="d-flex justify-content-center mb-3">
          <img
            src={avatarSrc}
            alt={t("passwordChange.avatarAlt")}
            style={{ width: "80px", height: "80px", borderRadius: "50%" }}
          />
        </div>

        {/* Welcome text */}
        <h4 className="fw-bold mb-1">
          {t("passwordChange.welcome", {
            name: companyName || t("passwordChange.userFallback")
          })}
        </h4>
        <p className="text-muted mb-4">{t("passwordChange.title")}</p>

        {/* Form */}
        <Formik
          initialValues={{ password: "", confirm: "" }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <form onSubmit={handleSubmit} style={{ padding: 32 }}>
              <div className="mb-3 text-start">
                <TextField
                  id="password"
                  type="password"
                  name="password"
                  label={t("passwordChange.fields.newPassword")}
                  placeholder={t("passwordChange.placeholders.newPassword")}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.password}
                  error={errors.password && touched.password}
                />
              </div>

              <div className="mb-4 text-start">
                <TextField
                  id="confirm"
                  type="password"
                  name="confirm"
                  label={t("passwordChange.fields.confirmPassword")}
                  placeholder={t("passwordChange.placeholders.confirmPassword")}
                  value={values.confirm}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors.confirm}
                  error={errors.confirm && touched.confirm}
                />
              </div>

              <button
                type="submit"
                className="btn w-100 text-white"
                style={{
                  backgroundColor: "#f15a24",
                  borderRadius: "25px"
                }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />{" "}
                    {t("passwordChange.actions.wait")}
                  </>
                ) : (
                  t("passwordChange.actions.confirm")
                )}
              </button>
            </form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
